// ===========================================
// Open Clinic Weeks Dialog (class detail page, admin)
// ===========================================
// The ONE admin door for opening a class's 1:1 clinic weeks to ANY term week
// (HybridLessonPattern.openClinicWeeks). Launched from the "Open weeks"
// affordance beside the Individual Weeks chips in the Class Details card.
// While week N is open, families ALREADY booked can move their session, and
// families NOT yet booked can book it — onto any day/time the teacher is
// available across the remaining term. Group weeks are never affected;
// clinic identity (clinicWeekNumber) and billing don't change. Opening
// requires confirming the locked availability warning.

import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useLessonBookings, useSetClinicWeekOpen } from '../../../hooks/useHybridBooking';
import { HybridBooking } from '../../../api/hybridBooking.api';

interface OpenClinicWeeksDialogProps {
  open: boolean;
  onClose: () => void;
  lessonId: string;
  /** The pattern's 1:1 clinic weeks (HybridLessonPattern.individualWeeks). */
  individualWeeks: number[];
  /** Clinic weeks currently open (HybridLessonPattern.openClinicWeeks). */
  openClinicWeeks: number[];
  teacherName?: string | null;
  /**
   * The term THIS lesson row belongs to. A multi-term class is separate rows
   * (one per term) — showing the term makes it unambiguous which term's
   * clinics are being opened.
   */
  termName?: string | null;
}

const ACTIVE_STATUSES = new Set(['PENDING', 'CONFIRMED']);

/** Active bookings FOR a clinic week (clinicWeekNumber ?? weekNumber). */
function bookingsForClinicWeek(bookings: HybridBooking[], week: number): HybridBooking[] {
  return bookings.filter(
    (b) => ACTIVE_STATUSES.has(b.status) && (b.clinicWeekNumber ?? b.weekNumber) === week
  );
}

export default function OpenClinicWeeksDialog({
  open,
  onClose,
  lessonId,
  individualWeeks,
  openClinicWeeks,
  teacherName,
  termName,
}: OpenClinicWeeksDialogProps) {
  const { data: lessonBookings } = useLessonBookings(open ? lessonId : '', {});
  const setClinicWeekOpen = useSetClinicWeekOpen();
  const [confirmWeek, setConfirmWeek] = useState<{ week: number; opening: boolean } | null>(null);

  const weekStates = useMemo(() => {
    const bookings = lessonBookings ?? [];
    return individualWeeks.map((week) => ({
      week,
      isOpen: openClinicWeeks.includes(week),
      bookedCount: bookingsForClinicWeek(bookings, week).length,
    }));
  }, [lessonBookings, individualWeeks, openClinicWeeks]);

  const handleConfirm = () => {
    if (!confirmWeek) return;
    setClinicWeekOpen.mutate(
      { lessonId, clinicWeek: confirmWeek.week, open: confirmWeek.opening },
      { onSuccess: () => setConfirmWeek(null) }
    );
  };

  const confirmState = confirmWeek
    ? weekStates.find((w) => w.week === confirmWeek.week)
    : undefined;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          Open 1:1 Clinic Weeks to Any Week
          {termName && (
            <Typography variant="body2" color="text.secondary">
              {termName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            If a clinic can't run in its scheduled week (e.g. the teacher is away), open it —
            families can then book or move that clinic session to any day and time the teacher is
            available, anywhere in the term. Group weeks are not affected.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {weekStates.map(({ week, isOpen, bookedCount }) => (
              <Box
                key={week}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography variant="body2">
                  Week {week}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    — {bookedCount} booked{isOpen ? ' · open' : ''}
                  </Typography>
                </Typography>
                <Tooltip
                  title={
                    isOpen
                      ? 'Close — this clinic returns to its scheduled week only'
                      : 'Open this clinic so families can book any week the teacher is available'
                  }
                >
                  <span>
                    <Switch
                      size="small"
                      checked={isOpen}
                      disabled={setClinicWeekOpen.isPending}
                      onChange={(e) => setConfirmWeek({ week, opening: e.target.checked })}
                      inputProps={{ 'aria-label': `Open week ${week} clinic to any week` }}
                    />
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog — opening carries the locked availability warning. */}
      <Dialog open={!!confirmWeek} onClose={() => setConfirmWeek(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmWeek?.opening
            ? `Open Week ${confirmWeek?.week} Clinic to Any Week?`
            : `Close Week ${confirmWeek?.week} Clinic?`}
        </DialogTitle>
        <DialogContent>
          {confirmWeek?.opening ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Families will be able to book (or move) their week {confirmWeek.week} clinic session
                onto <strong>any day and time the teacher is available</strong>, anywhere in the
                term{confirmState?.bookedCount ? ` — including the ${confirmState.bookedCount} already booked` : ''}.
                The clinic itself (and its billing) doesn't change — only when it runs.
              </Typography>
              <Alert severity="warning">
                Please ensure {teacherName ? `${teacherName}'s` : "the teacher's"} availability is
                correct before enabling this — including any approved time off — so families can't
                book a time the teacher is away.
              </Alert>
            </>
          ) : (
            <Typography variant="body2">
              New bookings return to the scheduled week only. Sessions already moved to other weeks
              stay exactly where they are.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmWeek(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmWeek?.opening ? 'warning' : 'primary'}
            onClick={handleConfirm}
            disabled={setClinicWeekOpen.isPending}
          >
            {setClinicWeekOpen.isPending
              ? 'Saving…'
              : confirmWeek?.opening
                ? 'Open to Any Week'
                : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
